import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const RANGES = [[24, "24 hours"], [48, "48 hours"], [168, "7 days"], [720, "30 days"]];
const KINDS = [["all", "All"], ["signins", "Sign-ins"], ["failed", "Failed sign-ins"], ["visits", "Page views"]];
const LABEL = {
  page_view: ["Page view", "text-slate-300 border-white/20"],
  login_success: ["Sign-in", "text-emerald-300 border-emerald-400/50"],
  google_login: ["Google sign-in", "text-emerald-300 border-emerald-400/50"],
  signup: ["Sign-up", "text-sky-300 border-sky-400/50"],
  login_failed: ["Failed sign-in", "text-rose-300 border-rose-400/50"],
};

let regionNames = null;
try { regionNames = new Intl.DisplayNames(["en"], { type: "region" }); } catch (e) { regionNames = null; }
const countryName = (c) => { if (!c) return ""; try { return regionNames?.of(c) || c; } catch (e) { return c; } };
const place = (r) => [r.city, r.region, countryName(r.country)].filter(Boolean).join(", ") || "Unknown";

function device(ua = "") {
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : /bot|crawl|spider/i.test(ua) ? "Bot" : "";
  return [br, os].filter(Boolean).join(" · ") || "Unknown";
}

const fmt = (d) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function ActivityLog() {
  const [hours, setHours] = useState(48);
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState(null);
  const [sum, setSum] = useState({});

  useEffect(() => {
    setRows(null);
    api.get("/admin/activity", { params: { hours, kind } }).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
    api.get("/admin/activity/summary", { params: { hours } }).then((r) => setSum(r.data || {})).catch(() => setSum({}));
  }, [hours, kind]);

  const stats = [["Page views", sum?.page_views ?? 0], ["Unique IPs", sum?.unique_ips ?? 0], ["Sign-ins", sum?.signins ?? 0], ["Failed sign-ins", sum?.failed ?? 0]];
  const countries = Array.isArray(sum?.countries) ? sum.countries : [];

  return (
    <div data-testid="activity-log">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {KINDS.map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`activity-kind-${k}`}
              className={`rounded-full border px-4 py-1.5 text-xs ${kind === k ? "border-white bg-white text-black" : "border-white/15 text-slate-300"}`}>{l}</button>
          ))}
        </div>
        <select className="field !w-auto !py-1.5 !text-xs" value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="activity-range">
          {RANGES.map(([h, l]) => <option key={h} value={h}>Last {l}</option>)}
        </select>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
        {stats.map(([l, v]) => (
          <div key={l} className="bg-[#080B11] p-4" data-testid={`activity-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className={`font-display text-2xl font-semibold ${l === "Failed sign-ins" && v > 0 ? "text-rose-300" : ""}`}>{v}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{l}</p>
          </div>
        ))}
      </div>

      {countries.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="activity-countries">
          {countries.map((c) => (
            <span key={c.country || "unknown"} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">
              {countryName(c.country) || "Unknown"} <span className="font-mono text-[var(--gold)]">{c.count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-6 overflow-x-auto border border-white/10" data-lenis-prevent>
        {rows === null ? (
          <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin text-sky-400" /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-400" data-testid="activity-empty">No activity in this period yet.</p>
        ) : (
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="activity-table">
            <thead className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <tr className="border-b border-white/10">{["Time", "Event", "Account", "IP address", "Location", "Page", "Device"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const [label, cls] = LABEL[r.type] || [r.type, "text-slate-300 border-white/20"];
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`activity-row-${i}`}>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-300">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3"><span className={`inline-block whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] ${cls}`}>{label}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-200">{r.email || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.ip || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-300">{place(r)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.path || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-400">{device(r.user_agent)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-400">Entries are kept for 90 days, then deleted automatically. Showing up to 1,000 most recent.</p>
    </div>
  );
}
